import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import useRequest from "@/lib/request/useRequest";
import {
    HarvestOperation,
    PlantingOperation,
    ListApplications,
    SectorSummary,
} from "@/types/operations";
import {
    faTrash,
    faPlusCircle,
    faFileImport,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    Badge,
    Button,
    Card,
    CardBody,
    DateRangePicker,
    DateValue,
    Divider,
    RangeValue,
} from "@heroui/react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { parseDate } from "@internationalized/date";
import ApplicationSummary from "@/components/field/operations/ApplicationSummary";
import IrrigationSummary from "@/components/field/operations/IrrigationSummary";
import { roundTo } from "@/lib/utils/numbers";
import { formatInputRates } from "@/lib/utils/operations";
export const Route = createFileRoute("/dashboard/fields/$fieldId/operations/")({
    component: RouteComponent,
});
import { useTranslation } from "react-i18next";
import useFeatureFlag from "@/lib/featureFlags/useFeatureFlags";
import { useI18n } from "@/lib/i18n/useI18n";
import useConfirm from "@/lib/confirm/useConfirm";
import { FieldConnection } from "@/types/connection";
import ImportOperation from "@/components/field/operations/ImportOperation";
import useAuth from "@/lib/auth/useAuth";
import dayjs from "dayjs";

function RouteComponent() {
    const navigate = useNavigate();
    const { formatUnit } = useI18n();
    const { useGetRequest, useMutateRequest } = useRequest();
    const { isConsultantOrSentinel } = useAuth();
    const { irrigationEnabled } = useFeatureFlag();
    const { fieldId, selectedCropCycle, fieldQuery } =
        useFieldDashboard() ?? {};

    const harvestOperationsQuery = useGetRequest<HarvestOperation[]>({
        url: `fields/${fieldId}/harvest_operations/`,
        enabled: !!fieldId && !!selectedCropCycle?.start_date,
        params: {
            start_date: selectedCropCycle?.start_date,
            end_date: selectedCropCycle?.end_date,
        },
    });

    const plantingOperationsQuery = useGetRequest<PlantingOperation[]>({
        url: `fields/${fieldId}/planting_operations/`,
        enabled: !!fieldId && !!selectedCropCycle?.start_date,
        params: {
            start_date: selectedCropCycle?.start_date,
            end_date: selectedCropCycle?.end_date,
        },
    });

    const [dates, setDates] = useState<RangeValue<DateValue> | null>({
        start: parseDate(
            selectedCropCycle?.start_date ??
            dayjs().subtract(3, "months").format("YYYY-MM-DD"),
        ),
        end: parseDate(
            selectedCropCycle?.end_date ?? dayjs().format("YYYY-MM-DD"),
        ),
    });

    const applicationOperationsQuery = useGetRequest<ListApplications>({
        url: `fields/${fieldId}/application_operations/`,
        enabled: !!fieldId && !!selectedCropCycle?.start_date,
        params: {
            start_date: dates?.start?.toString(),
            end_date: dates?.end?.toString(),
        },
    });

    const sectorSummaryQuery = useGetRequest<SectorSummary[]>({
        url: `fields/${fieldId}/application_operations/sector_summary/`,
        enabled: !!fieldId && !!selectedCropCycle?.start_date,
        params: {
            start_date: selectedCropCycle?.start_date,
            end_date: selectedCropCycle?.end_date,
        },
        queryOptions: {
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            refetchOnReconnect: false,
        },
    });

    const harvestOperation = useMemo(() => {
        if (selectedCropCycle?.effective_planting_operation?.start_date) {
            return harvestOperationsQuery?.data
                ?.filter(
                    (op) =>
                        op.start_date >=
                        selectedCropCycle?.effective_planting_operation
                            ?.start_date,
                )
                .sort((a, b) => (a.start_date > b.start_date ? -1 : 1))[0];
        }
        return harvestOperationsQuery?.data?.[0];
    }, [harvestOperationsQuery?.data, selectedCropCycle]);

    const hasDuplicateHarvestOperations = useMemo(() => {
        return harvestOperationsQuery?.data?.some((op) => {
            return (
                harvestOperationsQuery?.data?.filter(
                    (h) => h.start_date === op.start_date,
                ).length > 1
            );
        });
    }, [harvestOperationsQuery?.data]);

    const deleteHarvestOperation = useMutateRequest({
        url: harvestOperation
            ? `harvest_operations/${harvestOperation.id}/`
            : "",
        method: "delete",
    });

    const confirm = useConfirm();

    const handleHarvestDelete = () => {
        confirm.showConfirm({
            title: "Delete Operation",
            description: "Are you sure you want to delete this operation?",
            color: "danger",
            confirmText: "Delete",
            onConfirm: async () => {
                await deleteHarvestOperation.mutateAsync({});
                await harvestOperationsQuery.refetch?.();
            },
        });
    };

    const plantingOperation = useMemo(() => {
        if (selectedCropCycle?.planting_operation) {
            return plantingOperationsQuery?.data?.find(
                (op) => op.id === selectedCropCycle?.planting_operation,
            );
        }
        return plantingOperationsQuery?.data?.[0];
    }, [plantingOperationsQuery?.data, selectedCropCycle]);

    const hasDuplicatePlantingOperations = useMemo(() => {
        return plantingOperationsQuery?.data?.some((op) => {
            return (
                plantingOperationsQuery?.data?.filter(
                    (p) => p.start_date === op.start_date,
                ).length > 1
            );
        });
    }, [plantingOperationsQuery?.data]);

    const deletePlantingOperation = useMutateRequest({
        url: plantingOperation
            ? `planting_operations/${plantingOperation.id}/`
            : "",
        method: "delete",
    });

    const handlePlantingDelete = () => {
        confirm.showConfirm({
            title: "Delete Operation",
            description: "Are you sure you want to delete this operation?",
            color: "danger",
            confirmText: "Delete",
            onConfirm: async () => {
                await deletePlantingOperation.mutateAsync({});
                await plantingOperationsQuery.refetch?.();
            },
        });
    };

    useEffect(() => {
        if (!selectedCropCycle) return;

        setDates({
            start: parseDate(selectedCropCycle.start_date),
            end: selectedCropCycle.end_date
                ? parseDate(selectedCropCycle.end_date)
                : (null as unknown as DateValue),
        });
    }, [selectedCropCycle]);

    const { t } = useTranslation("operations");

    const fieldIsIrrigated = useMemo(() => {
        if (!fieldQuery?.data) return false;
        return fieldQuery.data.irrigation_systems.length > 0;
    }, [fieldQuery?.data]);

    const connectionsQuery = useGetRequest<FieldConnection[]>({
        url: "field_connections/",
        enabled: !!fieldId,
        params: {
            field: fieldId,
        },
    });
    const [selectedConnection, setSelectedConnection] =
        useState<FieldConnection | null>(null);

    return (
        <div className="flex flex-col gap-2 p-2 text-sm mb-[50px]">
            {selectedConnection && (
                <ImportOperation
                    connection={selectedConnection}
                    onClose={() => {
                        setSelectedConnection(null);
                        applicationOperationsQuery.refetch?.();
                    }}
                />
            )}
            <div className="flex flex-row items-center gap-2 w-full">
                <Button
                    fullWidth
                    color="secondary"
                    as={Link}
                    to={"/dashboard/fields/$fieldId/operations/add"}
                    radius="sm"
                >
                    <FontAwesomeIcon icon={faPlusCircle} />
                    {t("addOperation")}
                </Button>
                {connectionsQuery.data && connectionsQuery.data.length > 0 && (
                    <Button
                        fullWidth
                        color="secondary"
                        onPress={() =>
                            setSelectedConnection(connectionsQuery.data![0])
                        }
                        radius="sm"
                    >
                        <FontAwesomeIcon icon={faFileImport} /> Import Operation
                    </Button>
                )}
            </div>
            {harvestOperation && (
                <>
                    <div className="flex flex-row items-center justify-between w-full">
                        <Badge
                            color="warning"
                            content=""
                            isInvisible={!hasDuplicateHarvestOperations}
                        >
                            <span className="text-brandgreen-500 font-semibold text-lg">
                                {t("harvest")}
                            </span>
                        </Badge>
                    </div>

                    <Card
                        isPressable
                        onPress={() =>
                            navigate({
                                to: `/dashboard/fields/${fieldId}/operations/harvest/${harvestOperation.id}`,
                            })
                        }
                    >
                        <CardBody>
                            <div className="flex flex-row gap-2 w-full justify-between">
                                <div className="flex flex-col">
                                    <Badge
                                        color="warning"
                                        content="Duplicated"
                                        isInvisible={
                                            !hasDuplicateHarvestOperations
                                        }
                                        size="sm"
                                    >
                                        <div className="font-bold">
                                            {t("date")}
                                        </div>
                                    </Badge>
                                    {harvestOperation.start_date}
                                </div>
                                <div className="flex flex-col">
                                    <div className="font-bold">{t("crop")}</div>
                                    {plantingOperation?.crop_configuration
                                        ?.crop_name ?? "--"}
                                </div>
                                <div className="flex flex-col">
                                    <div className="font-bold">
                                        {t("yield")}
                                    </div>
                                    {formatInputRates(
                                        harvestOperation.input_rates,
                                        "rate_1",
                                        0,
                                    ) ??
                                        (harvestOperation.irrigated_averages
                                            ?.yield ??
                                            harvestOperation.field_averages
                                                ?.yield)}{" "}
                                    {t("yieldUnit")}
                                </div>
                                <div className="flex flex-col">
                                    <div className="font-bold">
                                        {t("moisture")}
                                    </div>
                                    {formatInputRates(
                                        harvestOperation.input_rates,
                                        "rate_2",
                                        0,
                                    ) ??
                                        (harvestOperation.irrigated_averages
                                            ?.moisture ??
                                            harvestOperation.field_averages
                                                ?.moisture)}
                                    %
                                </div>
                                {isConsultantOrSentinel && (
                                    <Button
                                        isIconOnly
                                        variant="solid"
                                        color="danger"
                                        size="sm"
                                        aria-label="Delete Harvest Operation"
                                        onPress={handleHarvestDelete}
                                        isLoading={
                                            deleteHarvestOperation.isPending
                                        }
                                        isDisabled={
                                            deleteHarvestOperation.isPending
                                        }
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </Button>
                                )}
                            </div>
                        </CardBody>
                    </Card>
                </>
            )}
            {plantingOperation && (
                <>
                    <div className="flex flex-row items-center gap-1 justify-between w-full">
                        <Badge
                            color="warning"
                            content=""
                            isInvisible={!hasDuplicatePlantingOperations}
                        >
                            <span className="text-brandgreen-500 font-semibold text-lg">
                                {t("planting")}
                            </span>
                        </Badge>
                    </div>
                    <Card
                        isPressable
                        onPress={() =>
                            navigate({
                                to: `/dashboard/fields/${fieldId}/operations/planting/${plantingOperation.id}`,
                            })
                        }
                    >
                        <CardBody>
                            <div className="flex flex-row gap-2 w-full justify-between">
                                <div className="flex flex-col">
                                    <Badge
                                        color="warning"
                                        content="Duplicated"
                                        isInvisible={
                                            !hasDuplicatePlantingOperations
                                        }
                                        size="sm"
                                    >
                                        <div className="font-bold">
                                            {t("date")}
                                        </div>
                                    </Badge>
                                    {plantingOperation.start_date}
                                </div>
                                <div className="flex flex-col">
                                    <div className="font-bold">{t("crop")}</div>
                                    {plantingOperation?.crop_configuration
                                        ?.crop_name ?? "--"}
                                </div>
                                <div className="flex flex-col">
                                    <div className="font-bold">
                                        {t("hybrid")}
                                    </div>
                                    {plantingOperation?.varieties?.[0]?.name ??
                                        "--"}
                                </div>
                                <div className="flex flex-col">
                                    <div className="font-bold">
                                        {t("seedingRate")}
                                    </div>
                                    {formatInputRates(
                                        plantingOperation.input_rates,
                                        "rate_1",
                                        0,
                                    ) ??
                                        roundTo(
                                            plantingOperation.average_seeding_rate ??
                                            0,
                                            0,
                                        )}{" "}
                                    {`seeds/${formatUnit("ac", "ha")}`}
                                </div>
                                {isConsultantOrSentinel && (
                                    <Button
                                        isIconOnly
                                        variant="solid"
                                        color="danger"
                                        size="sm"
                                        aria-label="Delete Planting Operation"
                                        onPress={handlePlantingDelete}
                                        isLoading={
                                            deletePlantingOperation.isPending
                                        }
                                        isDisabled={
                                            deletePlantingOperation.isPending
                                        }
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </Button>
                                )}
                            </div>
                        </CardBody>
                    </Card>
                </>
            )}
            <Divider className="my-2" />
            <DateRangePicker
                size="sm"
                label={t("startAndEndDate")}
                value={dates}
                aria-label={t("startAndEndDate")}
                onChange={setDates}
            />
            <ApplicationSummary
                fieldId={Number(fieldId!)}
                selectedCropCycle={selectedCropCycle!}
                applicationOperationsQuery={applicationOperationsQuery}
                sectorSummaryQuery={sectorSummaryQuery}
            />
            {irrigationEnabled && fieldIsIrrigated && (
                <IrrigationSummary
                    startDate={dates?.start?.toString()}
                    endDate={dates?.end?.toString()}
                />
            )}
        </div>
    );
}
